/**
 * Fase 5 — Classificador de itens (planilha + backend) por etapa canonica.
 *
 * Unifica 2 fontes em uma estrutura comum:
 *   - PLANILHA do Gerenciador (1 linha = 1 ambiente)
 *   - BACKEND Pedido (1 pedido = N PedidoAmbiente; cada ambiente vira 1 linha)
 *
 * Etapa atual:
 *   - Planilha: usa `getEtapaAtual` (ja existente em utils/toc/etapas.jsx)
 *     para producao; pre-producao usa campos da propria planilha
 *     (LIBERACAO_DA_OBRA, MEDICAO_REALIZADO, EXECUTIVO_REALIZADO, etc).
 *   - Backend: mapeia Pedido.situacao -> etapa canonica.
 *
 * Sem dedup: planilha e backend coexistem; UI mostra origem visualmente.
 */
import {
  ETAPA as ETAPA_PROD,
  getEtapaAtual,
  saidaExpedicao,
} from "../../utils/toc/etapas";
import {
  ETAPA_EXECUTIVO,
  entradaNaAprovacao,
  etapaDoExecutivo,
  limiteDaRevisao,
  usaOrdemNova,
} from "../../utils/fluxoExecutivo";
import { F } from "../../utils/gerencialFields";
import { hasValue } from "../../utils/data";
import { toDate } from "../../utils/status";

// Estados canonicos da Fase 5 — 7 etapas cobrindo o ciclo completo
export const ETAPA = {
  AGUARDANDO_LIBERACAO: "AGUARDANDO_LIBERACAO",
  EM_MEDICAO: "EM_MEDICAO",
  EM_EXECUTIVO: "EM_EXECUTIVO",
  // Só na ordem nova (revisão antes da aprovação); na antiga a revisão vem
  // depois da aprovação e o ambiente já conta como produção.
  EM_REVISAO: "EM_REVISAO",
  AGUARDANDO_APROVACAO: "AGUARDANDO_APROVACAO",
  EM_PRODUCAO: "EM_PRODUCAO",
  EM_EXPEDICAO: "EM_EXPEDICAO",
  ENTREGUE: "ENTREGUE",
};

export const ORDEM_ETAPAS = [
  ETAPA.AGUARDANDO_LIBERACAO,
  ETAPA.EM_MEDICAO,
  ETAPA.EM_EXECUTIVO,
  ETAPA.EM_REVISAO,
  ETAPA.AGUARDANDO_APROVACAO,
  ETAPA.EM_PRODUCAO,
  ETAPA.EM_EXPEDICAO,
  ETAPA.ENTREGUE,
];

export const ETAPA_LABEL = {
  [ETAPA.AGUARDANDO_LIBERACAO]: "Aguardando liberacao",
  [ETAPA.EM_MEDICAO]: "Em medicao",
  [ETAPA.EM_EXECUTIVO]: "Em executivo",
  [ETAPA.EM_REVISAO]: "Em revisão",
  [ETAPA.AGUARDANDO_APROVACAO]: "Aguardando aprovação",
  [ETAPA.EM_PRODUCAO]: "Em produção",
  [ETAPA.EM_EXPEDICAO]: "Em expedição",
  [ETAPA.ENTREGUE]: "Entregue",
};

// Tonalidade por etapa (paleta canonica: info=em progresso, warning=atencao, success=ok)
export const ETAPA_TONE = {
  [ETAPA.AGUARDANDO_LIBERACAO]: "neutral",
  [ETAPA.EM_MEDICAO]: "info",
  [ETAPA.EM_EXECUTIVO]: "info",
  [ETAPA.EM_REVISAO]: "info",
  [ETAPA.AGUARDANDO_APROVACAO]: "warning",
  [ETAPA.EM_PRODUCAO]: "info",
  [ETAPA.EM_EXPEDICAO]: "info",
  [ETAPA.ENTREGUE]: "success",
};

// Tonalidade por prioridade (mesma paleta de Orcamentos)
export const PRIORIDADE_TONE = {
  URGENTE: "danger",
  EXPRESS: "warning",
  NORMAL: "neutral",
  CORTESIA: "info",
};

// =====================================================================
// Helpers de data
// =====================================================================

function diasEntre(d1, d2) {
  if (!d1 || !d2) return null;
  const ms = d2.getTime() - d1.getTime();
  return Math.floor(ms / (1000 * 60 * 60 * 24));
}

function diasDesde(data) {
  if (!data) return null;
  const d = data instanceof Date ? data : toDate(data);
  if (!d) return null;
  return diasEntre(d, new Date());
}

// =====================================================================
// Classificacao de item da PLANILHA (amb = 1 linha da aba GERENCIAL)
// =====================================================================

/**
 * Etapa canonica de um ambiente da planilha + data de entrada na etapa.
 * Lógica "primeira etapa sem evento posterior vence", percorrendo os marcos
 * pré-aprovação primeiro, depois delegando à `getEtapaAtual` para produção.
 */
function etapaDePlanilha(amb) {
  // Terminal: ja saiu da expedicao
  if (saidaExpedicao(amb)) {
    return { etapa: ETAPA.ENTREGUE, entradaEm: toDate(amb?.[F.CARREGAMENTO]) };
  }

  // Pre-producao: LIBERACAO -> MEDICAO -> EXECUTIVO -> (REVISAO) -> APROVACAO -> PRODUCAO
  const liberacao = toDate(amb?.[F.LIBERACAO_DA_OBRA]);
  if (!liberacao) {
    return {
      etapa: ETAPA.AGUARDANDO_LIBERACAO,
      entradaEm: toDate(amb?.[F.DATA_DA_VENDA]),
    };
  }

  const medicao = toDate(amb?.[F.MEDICAO_REALIZADO]);
  if (!medicao) {
    return { etapa: ETAPA.EM_MEDICAO, entradaEm: liberacao };
  }

  const executivo = toDate(amb?.[F.EXECUTIVO_REALIZADO]);
  if (!executivo) {
    return { etapa: ETAPA.EM_EXECUTIVO, entradaEm: medicao };
  }

  // Revisao e aprovacao na ordem da linha (utils/fluxoExecutivo). Na antiga,
  // o aprovado sem revisao segue como producao, como sempre foi.
  const etapaExecutivo = etapaDoExecutivo(amb);
  if (etapaExecutivo === ETAPA_EXECUTIVO.REVISAO && usaOrdemNova(amb)) {
    return { etapa: ETAPA.EM_REVISAO, entradaEm: executivo };
  }
  if (etapaExecutivo === ETAPA_EXECUTIVO.APROVACAO) {
    return { etapa: ETAPA.AGUARDANDO_APROVACAO, entradaEm: toDate(entradaNaAprovacao(amb)) };
  }
  const aprovacao = toDate(amb?.[F.APROVACAO_DO_EXECUTIVO]);

  // Producao: usa getEtapaAtual
  const etapaProd = getEtapaAtual(amb);
  if (!etapaProd) {
    // Sem etapa de producao identificada mas aprovado — assume "em producao"
    return { etapa: ETAPA.EM_PRODUCAO, entradaEm: aprovacao };
  }

  // EXPEDICAO/ACABAMENTO -> EM_EXPEDICAO canonical
  if (
    etapaProd === ETAPA_PROD.EXPEDICAO ||
    etapaProd === ETAPA_PROD.ACABAMENTO
  ) {
    return { etapa: ETAPA.EM_EXPEDICAO, entradaEm: aprovacao };
  }

  // Demais (COMPRAS, MAQUINAS, TERCEIROS, MARCENARIA) -> EM_PRODUCAO
  return { etapa: ETAPA.EM_PRODUCAO, entradaEm: aprovacao };
}

/**
 * Calcula atraso em dias — usa LIMITE_* mais relevante para a etapa atual.
 * Retorna numero positivo se atrasado, 0 se no prazo, negativo se folga,
 * ou null se nao ha limite definido para a etapa.
 */
function atrasoDeAmbiente(amb, etapa) {
  const hoje = new Date();
  let limite = null;
  switch (etapa) {
    case ETAPA.EM_MEDICAO:
      limite = toDate(amb?.[F.MEDICAO_LIMITE]);
      break;
    case ETAPA.EM_EXECUTIVO:
      limite = toDate(amb?.[F.EXECUTIVO_LIMITE]);
      break;
    case ETAPA.EM_REVISAO:
      limite = limiteDaRevisao(amb);
      break;
    case ETAPA.AGUARDANDO_APROVACAO:
      limite = toDate(amb?.[F.APROVACAO_LIMITE]);
      break;
    case ETAPA.EM_PRODUCAO:
    case ETAPA.EM_EXPEDICAO:
      limite = toDate(amb?.[F.ENTREGA_PREVISTA]);
      break;
    default:
      return null;
  }
  if (!limite) return null;
  return diasEntre(limite, hoje); // positivo = atrasado
}

function prioridadeDeAmbiente(amb) {
  // Planilha nao tem prioridade explicita; OBRA_FUTURA marca "urgente" indireto
  // (obra prevista — vendedor priorizou). Fallback NORMAL.
  if (hasValue(amb?.[F.OBRA_FUTURA])) return "URGENTE";
  return "NORMAL";
}

// =====================================================================
// Classificacao de PEDIDO do backend
// =====================================================================

const SITUACAO_BACKEND_PARA_ETAPA = {
  RECEBIDO: ETAPA.AGUARDANDO_LIBERACAO,
  EM_PRODUCAO: ETAPA.EM_PRODUCAO,
  EM_EXPEDICAO: ETAPA.EM_EXPEDICAO,
  ENTREGUE: ETAPA.ENTREGUE,
  // CANCELADO nao aparece no painel
};

function entradaDePedidoBackend(pedido, etapaCanonica) {
  switch (etapaCanonica) {
    case ETAPA.AGUARDANDO_LIBERACAO:
      return toDate(pedido?.created_at);
    case ETAPA.EM_PRODUCAO:
      return toDate(pedido?.iniciado_producao_em);
    case ETAPA.EM_EXPEDICAO:
      return toDate(pedido?.iniciado_expedicao_em);
    case ETAPA.ENTREGUE:
      return toDate(pedido?.entregue_em);
    default:
      return toDate(pedido?.created_at);
  }
}

// =====================================================================
// API publica
// =====================================================================

/**
 * Classifica um item da planilha (linha do GERENCIAL = 1 ambiente).
 * Retorna shape canonica `{etapa, entradaEm, diasNaEtapa, prioridade, atrasoDias, origem, raw}`.
 */
export function classificarAmbientePlanilha(amb) {
  const { etapa, entradaEm } = etapaDePlanilha(amb);
  const diasNaEtapa = diasDesde(entradaEm);
  const atrasoDias = atrasoDeAmbiente(amb, etapa);
  const prioridade = prioridadeDeAmbiente(amb);
  return {
    id: `planilha:${amb?.[F.NUMERO_DO_PROJETO] || ""}:${amb?.[F.AMBIENTE] || ""}`,
    origem: "planilha",
    numeroProjeto: amb?.[F.NUMERO_DO_PROJETO] || "—",
    numeroPedido: amb?.[F.NUMERO_DO_PEDIDO] || null,
    cliente: amb?.[F.NOME_DO_CLIENTE] || "—",
    ambiente: amb?.[F.AMBIENTE] || "—",
    etapa,
    entradaEm,
    diasNaEtapa,
    prioridade,
    atrasoDias,
    raw: amb,
  };
}

/**
 * Classifica um pedido do backend — cria 1 entrada por ambiente do pedido.
 * Recebe `pedido` com `ambientes: [{ id, nome, ... }]` (ja embedded no Detalhe).
 * Quando ambientes vazios, gera 1 entrada com ambiente="(sem ambientes)".
 */
export function classificarPedidoBackend(pedido) {
  if (pedido.situacao === "CANCELADO") return [];
  const etapa = SITUACAO_BACKEND_PARA_ETAPA[pedido.situacao] || ETAPA.AGUARDANDO_LIBERACAO;
  const entradaEm = entradaDePedidoBackend(pedido, etapa);
  const diasNaEtapa = diasDesde(entradaEm);
  const prioridade = pedido.prioridade || "NORMAL";
  const ambientes = pedido.ambientes && pedido.ambientes.length > 0
    ? pedido.ambientes
    : [{ id: null, nome: "(sem ambientes)" }];

  return ambientes.map((amb) => ({
    id: `backend:${pedido.id}:${amb.id || "0"}`,
    origem: "backend",
    numeroProjeto: pedido.numero || "—", // ex PV-UN1-1018
    numeroPedido: pedido.numero || null,
    cliente: pedido.cliente_nome || pedido.cliente?.nome || "—",
    ambiente: amb.nome || "—",
    etapa,
    entradaEm,
    diasNaEtapa,
    prioridade,
    atrasoDias: null, // backend nao tem prazo_prometido ainda (lacuna conhecida)
    raw: { pedido, ambiente: amb },
  }));
}

/**
 * Une as 2 fontes em uma lista plana ordenada por (atraso desc, dias na etapa desc).
 */
export function consolidarAcompanhamento({ dadosPlanilha = [], pedidosBackend = [] }) {
  const planilha = (dadosPlanilha || [])
    .filter((amb) => amb && (amb[F.NOME_DO_CLIENTE] || amb[F.AMBIENTE]))
    .map(classificarAmbientePlanilha);
  const backend = (pedidosBackend || []).flatMap(classificarPedidoBackend);
  const todos = [...planilha, ...backend];
  // Ordena: atrasados primeiro, depois mais tempo na etapa
  todos.sort((a, b) => {
    const aAtraso = a.atrasoDias ?? -Infinity;
    const bAtraso = b.atrasoDias ?? -Infinity;
    if (aAtraso !== bAtraso) return bAtraso - aAtraso;
    return (b.diasNaEtapa ?? 0) - (a.diasNaEtapa ?? 0);
  });
  return todos;
}

/**
 * Conta itens por etapa + atrasados + urgentes (alimenta KpiStrip).
 */
export function contarPorBucket(itens) {
  const counts = {
    total: 0,
    porEtapa: {},
    atrasados: 0,
    urgentes: 0,
  };
  ORDEM_ETAPAS.forEach((e) => {
    counts.porEtapa[e] = 0;
  });
  itens.forEach((it) => {
    counts.total += 1;
    counts.porEtapa[it.etapa] = (counts.porEtapa[it.etapa] || 0) + 1;
    if (it.atrasoDias !== null && it.atrasoDias > 0) counts.atrasados += 1;
    if (it.prioridade === "URGENTE" || it.prioridade === "EXPRESS") counts.urgentes += 1;
  });
  return counts;
}
